// A design template is an admin-authored design that a customer applies to a
// section and then fills in. The stored payload carries no section identity:
// a template is applied away from wherever it was authored, so the destination
// section supplies its own name, material and geometry at apply time.

export interface TemplateSectionLink {
  id: string
  name: string
}

/** Where the template was authored. Metadata only; nothing here places the design. */
export interface TemplateOrigin {
  sku?: string
  product_id?: string
  product_profile_id?: string
  section_id?: string
  section_name?: string
  /** Element images follow the export format of the profile they were authored on. */
  profile_type?: 'laser' | 'color'
  profile_view?: '2D' | '3D'
}

/** The geometry the design was drawn against, in mm. The rescale anchors. */
export interface TemplateCanvas {
  width: number
  height: number
  offset_x: number
  offset_Y: number
}

export type TemplateInputType = 'text' | 'date' | 'image' | 'graphic'

/** The tag that marks an element as something the customer must fill in. */
export interface TemplatePlaceholder {
  placeholder: boolean
  key: string
  label: string
  labels?: Record<string, string>
  input: TemplateInputType
  required?: boolean
  order?: number
  help?: string
  /** What the admin typed. The panel treats an unchanged value as "not filled yet". */
  sample?: string
  max_chars?: number
  /** Only meaningful when input is 'date'. */
  format?: string
}

export interface TemplateElement {
  elementId: string
  type: string
  template?: TemplatePlaceholder
  [key: string]: any
}

export interface DesignTemplateData {
  version: number
  origin?: TemplateOrigin
  canvas?: TemplateCanvas
  elements: TemplateElement[]
}

/** Summary shape returned by the listing. Carries no design payload. */
export interface DesignTemplate {
  id: string
  name: string
  description: string
  thumbnail_url: string
  is_active: boolean
  is_generic: boolean
  priority: number
  version: number
  sections: TemplateSectionLink[]
  /** Summarised server side so a listing needs no extra request per row. */
  element_count: number
  placeholder_count: number
  origin?: TemplateOrigin
}

export interface DesignTemplateDetail extends DesignTemplate {
  data: DesignTemplateData
}

/** The flattened shape the admin UI works with. */
export interface FlattedDesignTemplate {
  id: string
  name: string
  description: string
  thumbnail: string
  isActive: boolean
  isGeneric: boolean
  priority: number
  version: number
  sections: TemplateSectionLink[]
  placeholderCount: number
  elementCount: number
  origin?: TemplateOrigin
}

export interface DesignTemplateFormValues {
  name: string
  description?: string
  isActive: boolean
  isGeneric: boolean
  priority?: number
}

export interface SaveDesignTemplateRequest {
  name: string
  description?: string
  thumbnail_url?: string
  is_active: boolean
  is_generic: boolean
  priority: number
  data?: DesignTemplateData
  section_ids?: string[]
}

export const EMPTY_TEMPLATE_DATA: DesignTemplateData = {
  version: 1,
  elements: [],
}

/**
 * Fields a saved design carries because it was sitting on a section. A template
 * has no home section, so these must never be stored: they would be stale the
 * first time the template is applied somewhere else.
 */
export const SECTION_IDENTITY_FIELDS = [
  'section_id',
  'section_name',
  'section_angle',
  'radial_angle',
  'section_coordinates',
  'section_dimensions',
  'product_sku',
  'product_profile_id',
  'bundle_index',
  'snapshot_viewport',
  'is_full_wrap',
  'material_type',
  'engraving_tint',
  'overlay_opacity',
  'artwork_coordinates',
  'artwork_dimensions',
  'is_text_needed_to_check',
  'sections',
] as const
